<template>
  <article class="product-card">
    <div class="product-card__visual" :class="`tone-${tone}`">
      <span v-if="product.isNew" class="product-card__badge">New</span>
      <PerfumeBottle
        :name="product.name"
        :meta="variant.type"
        :deep="selectedForm === 'Ittar'"
        :scale="0.62"
      />
      <span class="product-card__number">{{ number }}</span>
    </div>

    <div class="product-card__content">
      <p class="product-card__eyebrow">Inspired by {{ product.inspiredBy }}</p>
      <h3>{{ product.name }}</h3>

      <ul class="note-chips" aria-label="Fragrance notes">
        <li v-for="note in noteChips" :key="note.label" :title="note.label">
          <v-icon size="13">{{ note.icon }}</v-icon>
          <span>{{ note.label }}</span>
        </li>
      </ul>

      <div class="choice-group" aria-label="Fragrance form">
        <button
          v-for="option in forms"
          :key="option"
          type="button"
          :class="{ active: selectedForm === option }"
          @click="selectedForm = option"
        >
          {{ option }}
        </button>
      </div>

      <div v-if="selectedForm === 'Perfume'" class="choice-group choice-group--size" aria-label="Perfume size">
        <button
          v-for="option in sizes"
          :key="option"
          type="button"
          :class="{ active: selectedSize === option }"
          @click="selectedSize = option"
        >
          {{ option }}
        </button>
      </div>
      <p v-else class="ittar-note">Concentrated roll-on · 6 ml</p>

      <div class="product-card__footer">
        <div>
          <strong>{{ formattedPrice }}</strong>
          <small>{{ variantLabel }}</small>
        </div>
        <button class="add-button" type="button" @click="addToCart">
          Add <span aria-hidden="true">+</span>
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import PerfumeBottle from './PerfumeBottle.vue'
import { fragranceVariants, formatPrice, getVariantPrice } from '../data/products'
import { getNoteChips } from '../utils/notes'

export default {
  name: 'ProductCard',
  components: { PerfumeBottle },
  props: {
    product: { type: Object, required: true },
    number: { type: String, default: '01' },
    tone: { type: Number, default: 0 }
  },
  data: () => ({
    selectedForm: 'Perfume',
    selectedSize: '10 ml',
    forms: ['Perfume', 'Ittar'],
    sizes: ['10 ml', '50 ml']
  }),
  computed: {
    variant() {
      if (this.selectedForm === 'Ittar') return fragranceVariants.ittar
      return this.selectedSize === '50 ml'
        ? fragranceVariants.perfume50
        : fragranceVariants.perfume10
    },
    price() {
      return getVariantPrice(this.product, this.variant)
    },
    formattedPrice() {
      return formatPrice(this.price)
    },
    variantLabel() {
      return `${this.variant.type} · ${this.variant.size}`
    },
    noteChips() {
      return getNoteChips(this.product.notes)
    }
  },
  methods: {
    addToCart() {
      this.$emit('add-to-cart', {
        cartKey: `${this.product.id}-${this.variant.id}`,
        productId: this.product.id,
        name: this.product.name,
        variantLabel: this.variantLabel,
        price: this.price
      })
    }
  }
}
</script>

<style scoped>
.product-card {
  background: #fff;
  border: 1px solid #e7e1d8;
  border-radius: 16px;
  overflow: hidden;
  transition: transform .2s ease, box-shadow .2s ease;
}
.product-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(38, 29, 20, .08); }
.product-card__visual {
  min-height: 168px;
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: linear-gradient(180deg, #eee4d7, #e0d2be);
}
.product-card__visual::after {
  content: '';
  position: absolute;
  width: 140px;
  height: 140px;
  border: 1px solid rgba(255,255,255,.65);
  border-radius: 50%;
}
.product-card__visual .bottle { z-index: 1; }
.tone-1 { background: linear-gradient(180deg, #e4e7dd, #d3d8ca); }
.tone-2 { background: linear-gradient(180deg, #e7dad9, #d8c8c8); }
.tone-3 { background: linear-gradient(180deg, #dfe1e4, #ccd0d5); }
.product-card__badge,
.product-card__number {
  position: absolute;
  z-index: 2;
  font-size: 9px;
  letter-spacing: .8px;
  text-transform: uppercase;
}
.product-card__badge {
  top: 10px;
  left: 10px;
  padding: 4px 8px;
  color: #fff;
  background: #25221e;
  border-radius: 14px;
}
.product-card__number {
  right: 10px;
  bottom: 8px;
  color: rgba(37,34,30,.45);
}
.product-card__content { padding: 12px 14px 14px; }
.product-card__eyebrow {
  margin: 0 0 2px;
  color: #8a7f72;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .9px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
h3 {
  margin: 0;
  color: #211e1a;
  font-family: Georgia, serif;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.note-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 8px 0 10px;
  padding: 0;
  list-style: none;
}
.note-chips li {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  border: 1px solid #ebe4db;
  border-radius: 999px;
  color: #6f655b;
  background: #faf7f2;
  font-size: 10px;
  font-weight: 600;
}
.choice-group { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; }
.choice-group--size { margin-top: 5px; }
.choice-group button {
  height: 28px;
  border: 1px solid #ded7cd;
  border-radius: 7px;
  color: #71685e;
  background: #fff;
  font-size: 11px;
}
.choice-group button.active { border-color: #26221d; color: #fff; background: #26221d; }
.ittar-note {
  height: 28px;
  margin: 5px 0 0;
  display: flex;
  align-items: center;
  color: #8b5e3c;
  font-size: 10px;
}
.product-card__footer {
  display: flex;
  align-items: end;
  justify-content: space-between;
  margin-top: 12px;
}
.product-card__footer strong { display: block; color: #211e1a; font-size: 16px; line-height: 1.1; }
.product-card__footer small { color: #8b8176; font-size: 9px; }
.add-button {
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #b06f45;
  font-size: 12px;
  font-weight: 700;
}
.add-button span { margin-left: 5px; font-size: 15px; line-height: 0; }
@media (max-width: 600px) {
  .product-card__visual { min-height: 156px; }
  .product-card__content { padding: 11px 12px 12px; }
  h3 { font-size: 16px; }
}
</style>
